import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Circle,
  X,
  ListChecks,
  ExternalLink,
  RotateCcw,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import {
  getRecentAnalyticsEvents,
  type DebugAnalyticsEvent,
} from "@/lib/analytics";

const STORAGE_KEY = "analytics_qa_enabled";

interface QAStep {
  id: string;
  title: string;
  instructions: string;
  expectedEvents: string[];
  /** Optional extra check on params (e.g. form_name === 'rideshare_application') */
  paramMatch?: (params: Record<string, unknown>) => boolean;
  /** Optional CTA link rendered next to the step */
  link?: { label: string; to: string; external?: boolean };
}

const STEPS: QAStep[] = [
  {
    id: "home_pageview",
    title: "Visit the homepage",
    instructions: "Click the link, or refresh /. A page_view should fire on initial load (or on a route change).",
    expectedEvents: ["page_view"],
    link: { label: "Open /", to: "/" },
  },
  {
    id: "phone_click",
    title: "Click a phone number",
    instructions: "Tap the (201) 589-2177 link in the navbar or footer. We capture all tel: links via a delegated listener.",
    expectedEvents: ["phone_click"],
  },
  {
    id: "email_click",
    title: "Click an email link",
    instructions: "Click the info@moderncarrental.com link in the footer or contact section.",
    expectedEvents: ["email_click"],
  },
  {
    id: "booking_widget_view",
    title: "Scroll to the booking widget",
    instructions: "Scroll to the #booking section on the homepage (or jump there). booking_widget_view fires when it mounts.",
    expectedEvents: ["booking_widget_view"],
    link: { label: "Open #booking", to: "/#booking" },
  },
  {
    id: "booking_search_click",
    title: "Click Search/Book in the widget",
    instructions: "Inside the Wheelbase widget, click the main Search / Book Now / Check Availability button. NOTE: only works on the published site — preview can't render the widget.",
    expectedEvents: ["booking_widget_search_click"],
  },
  {
    id: "rideshare_pageview",
    title: "Open the rideshare page",
    instructions: "Navigate to /rideshare. Confirm a page_view fires on the SPA route change.",
    expectedEvents: ["page_view"],
    paramMatch: (p) => typeof p.page_path === "string" && p.page_path.startsWith("/rideshare"),
    link: { label: "Open /rideshare", to: "/rideshare" },
  },
  {
    id: "rideshare_form_start",
    title: "Start filling the rideshare form",
    instructions: "Click into any field (e.g. Full Name) and type a character. rideshare_form_start fires once on first interaction.",
    expectedEvents: ["rideshare_form_start"],
  },
  {
    id: "rideshare_step_complete",
    title: "Complete the personal info step",
    instructions: "Fill Full Name, Email, and Phone. As soon as all three are non-empty, rideshare_form_step_complete fires for step 1 (personal).",
    expectedEvents: ["rideshare_form_step_complete"],
    paramMatch: (p) => p.step_index === 1 || p.step_name === "personal",
  },
  {
    id: "rideshare_field_error",
    title: "Trigger a field validation error",
    instructions: "Type an invalid email (e.g. 'foo') and tab out. rideshare_form_field_error should fire on blur with field_name=email.",
    expectedEvents: ["rideshare_form_field_error"],
    paramMatch: (p) => p.field_name === "email",
  },
  {
    id: "lead_quote_submit",
    title: "Submit the Get a Quote form",
    instructions: "On the homepage, scroll to the Get a Quote form, fill it with valid data, and submit. lead_form_submit + generate_lead both fire with form_name=lead_capture_quote.",
    expectedEvents: ["lead_form_submit", "generate_lead"],
    paramMatch: (p) => p.form_name === "lead_capture_quote",
    link: { label: "Open /#contact", to: "/#contact" },
  },
];

function eventMatches(ev: DebugAnalyticsEvent, step: QAStep) {
  if (!step.expectedEvents.includes(ev.name)) return false;
  if (step.paramMatch && !step.paramMatch(ev.params as Record<string, unknown>)) return false;
  return true;
}

function isQAEnabled(): boolean {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("qa") === "analytics") {
      localStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    if (url.searchParams.get("qa") === "off") {
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export default function AnalyticsQAChecklist() {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(true);
  const [completed, setCompleted] = useState<Record<string, number>>({});
  const [expandedStep, setExpandedStep] = useState<string | null>(STEPS[0]?.id ?? null);
  const startTime = useRef<number>(Date.now());

  useEffect(() => {
    setEnabled(isQAEnabled());
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "q") {
        e.preventDefault();
        const next = !isQAEnabled();
        if (next) localStorage.setItem(STORAGE_KEY, "1");
        else localStorage.removeItem(STORAGE_KEY);
        setEnabled(next);
        if (next) setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Watch the analytics event stream and auto-tick steps as their expected
  // events arrive. Only events fired AFTER the QA panel was opened count.
  useEffect(() => {
    if (!enabled) return;

    const checkBuffer = () => {
      const recent = getRecentAnalyticsEvents();
      setCompleted((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const step of STEPS) {
          if (next[step.id]) continue;
          const match = recent.find(
            (ev) => ev.timestamp >= startTime.current && eventMatches(ev, step),
          );
          if (match) {
            next[step.id] = match.timestamp;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };

    checkBuffer();
    const onEvent = () => checkBuffer();
    window.addEventListener("analytics:event", onEvent);
    return () => window.removeEventListener("analytics:event", onEvent);
  }, [enabled]);

  const stats = useMemo(() => {
    const done = STEPS.filter((s) => completed[s.id]).length;
    return { done, total: STEPS.length, pct: Math.round((done / STEPS.length) * 100) };
  }, [completed]);

  if (!enabled) return null;

  const reset = () => {
    setCompleted({});
    startTime.current = Date.now();
  };

  return (
    <div
      className="fixed bottom-4 left-4 z-[9999] w-[400px] max-w-[calc(100vw-2rem)] rounded-xl border border-border/60 bg-background/95 backdrop-blur-xl shadow-2xl font-sans"
      role="region"
      aria-label="Analytics QA checklist"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/40">
        <ListChecks className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold text-foreground">Analytics QA</span>
        <span className="text-[10px] text-muted-foreground tabular-nums">
          {stats.done}/{stats.total}
        </span>
        <div className="flex-1 h-1 rounded-full bg-secondary/60 overflow-hidden ml-1">
          <div
            className="h-full bg-primary transition-all duration-500"
            style={{ width: `${stats.pct}%` }}
          />
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-muted-foreground hover:text-foreground"
          aria-label={open ? "Collapse" : "Expand"}
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={reset}
          className="text-muted-foreground hover:text-foreground"
          title="Reset checklist (re-arm event listeners)"
          aria-label="Reset checklist"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => {
            localStorage.removeItem(STORAGE_KEY);
            setEnabled(false);
          }}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close QA panel"
          title="Close (Ctrl/Cmd + Shift + Q to re-open)"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {open && (
        <>
          <div className="max-h-[60vh] overflow-y-auto">
            {STEPS.map((step, i) => {
              const isDone = !!completed[step.id];
              const isExpanded = expandedStep === step.id;
              return (
                <div key={step.id} className="border-b border-border/40 last:border-b-0">
                  <button
                    type="button"
                    onClick={() => setExpandedStep(isExpanded ? null : step.id)}
                    className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-secondary/40 transition-colors"
                  >
                    {isDone ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                    ) : (
                      <Circle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] tabular-nums text-muted-foreground">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span
                          className={`text-xs font-medium ${
                            isDone ? "text-emerald-400 line-through" : "text-foreground"
                          }`}
                        >
                          {step.title}
                        </span>
                      </div>
                      {isExpanded && (
                        <div className="mt-2 space-y-2">
                          <p className="text-[11px] text-muted-foreground leading-relaxed">
                            {step.instructions}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] text-muted-foreground">expects:</span>
                            {step.expectedEvents.map((ev) => (
                              <code
                                key={ev}
                                className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary/60 border border-border/40 text-foreground"
                              >
                                {ev}
                              </code>
                            ))}
                          </div>
                          {step.link && (
                            <Link
                              to={step.link.to}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                            >
                              {step.link.label}
                              <ExternalLink className="h-3 w-3" />
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  </button>
                </div>
              );
            })}
          </div>

          <div className="px-3 py-2 border-t border-border/40 space-y-1.5">
            <a
              href="https://analytics.google.com/analytics/web/#/p0/realtime/overview"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
            >
              Open GA4 Realtime
              <ExternalLink className="h-3 w-3" />
            </a>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Auto-ticks confirm the event fired on this page. Cross-check the same event names in GA4 → Realtime → Event count by Event name to confirm Google received them.{" "}
              <kbd className="font-mono px-1 py-0.5 bg-secondary/60 rounded border border-border/40">
                Ctrl/Cmd + Shift + Q
              </kbd>{" "}
              to toggle.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
